import React, { useState, useContext } from "react";
import {
  makeStyles,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";

import StepTemplates from "./StepTemplates";
import StepAvatar from "./StepAvatar";
import StepVoice from "./StepVoice";
import StepScript from "./StepScript";
import StepPreview from "./StepPreview"; // Refactored step 5
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  actionsContainer: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "space-between",
  },
}));

const steps = ["Plantilla", "Personaje y Marca", "Voz", "Guion", "Generar"];

const AiVideoWizard = ({ onVideoGenerated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [activeStep, setActiveStep] = useState(0);

  // Form Data State
  const [formData, setFormData] = useState({
    format: null,
    avatarId: null, // Si elige de stock
    avatarFile: null, // Si sube propio
    logoFile: null,
    voiceActor: "alloy", // O id de stock
    voiceSampleFile: null,
    script: "",
    useSubtitles: true,
    useMusic: true,
    musicTrack: "corporate"
  });

  const updateFormData = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  const handleGenerate = async () => {
    if (!formData.script.trim()) {
      toast.error("Por favor, ingresa un guion en el paso 4.");
      return;
    }
    
    setIsGenerating(true);
    setVideoUrl("");
    toast.info("Enviando petición... Esto tomará unos minutos.", { autoClose: 5000 });
    
    try {
      const form = new FormData();
      form.append("prompt", formData.script);
      form.append("format", formData.format);
      form.append("aspectRatio", formData.format === "ugc" ? "9:16" : "16:9");
      form.append("duration", "5");
      form.append("sound", formData.useMusic);
      form.append("voiceActor", formData.voiceActor);
      form.append("useSubtitles", formData.useSubtitles);
      form.append("musicTrack", formData.musicTrack);
      if (formData.avatarId) form.append("avatarId", formData.avatarId);

      if (formData.avatarFile) form.append("avatar", formData.avatarFile);
      if (formData.logoFile) form.append("logo", formData.logoFile);
      if (formData.voiceSampleFile) form.append("voice_sample", formData.voiceSampleFile);

      const response = await api.post("/ai-video/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || "Error en la petición.");
      }
      
      // La notificación de éxito llegará por WebSockets (como está en el useEffect del index principal o aquí)
      // Por brevedad, asumimos que se maneja igual que antes.
    } catch (err) {
      setIsGenerating(false);
      toast.error(err.response?.data?.error || err.message || "Error al solicitar video.");
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <StepTemplates formData={formData} updateFormData={updateFormData} />;
      case 1:
        return <StepAvatar formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <StepVoice formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <StepScript formData={formData} updateFormData={updateFormData} />;
      case 4:
        return (
          <StepPreview 
            formData={formData} 
            isGenerating={isGenerating} 
            videoUrl={videoUrl}
            setVideoUrl={setVideoUrl}
            setIsGenerating={setIsGenerating}
          />
        );
      default:
        return "Paso desconocido";
    }
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Typography variant="h5" color="primary" gutterBottom>
        Generador de Video IA (Asistente Mágico)
      </Typography>
      
      <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>
        {getStepContent(activeStep)}
        
        <div className={classes.actionsContainer}>
          <Button
            variant="contained"
            disabled={activeStep === 0 || isGenerating}
            onClick={handleBack}
            className={classes.secondaryButton}
          >
            Atrás
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              className={classes.applyButton}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? "Generando..." : "Generar Video Mágico"}
            </Button>
          ) : (
            <Button
              variant="contained"
              className={classes.applyButton}
              onClick={handleNext}
              disabled={activeStep === 0 && !formData.format}
            >
              Siguiente
            </Button>
          )}
        </div>
      </Box>
    </Paper>
  );
};

export default AiVideoWizard;
